import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Pencil, X } from "lucide-react";

export type GuardPerson = { id: string; name: string; sub?: string | null };

type Props = {
  open: boolean;
  onClose: () => void;
  /** Drives the wording: "access" or "role". */
  entity: "access" | "role";
  /** Name of the access / role the user tried to delete. */
  name: string;
  people: GuardPerson[];
  onEditPerson: (id: string) => void;
};

/**
 * Centered, animated "this is still in use — can't delete it" popup.
 *
 * Replaces the old top-right toast for the delete guard. Portaled to <body>
 * so it centers on the viewport regardless of any transformed ancestor (the
 * settings pages animate their cards with `transform`, which would otherwise
 * trap a `position: fixed` child). Lists the assigned people as clickable rows
 * so the manager can jump straight to reassigning them.
 */
export function InUseGuardModal({ open, onClose, entity, name, people, onEditPerson }: Props) {
  // Escape to close + lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const count = people.length;
  const label = entity === "role" ? "role" : "access";

  return createPortal(
    <div
      className="igm-overlay fixed inset-0 z-[100] flex items-center justify-center p-4"
      onMouseDown={onClose}
      // Inline so the global themed-dialog rules can't override the scrim.
      style={{
        background: "rgba(17,24,20,.45)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
    >
      <style>{`
        @keyframes igmFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes igmPop {
          from { opacity: 0; transform: translateY(12px) scale(.94); }
          to   { opacity: 1; transform: translateY(0)    scale(1);   }
        }
        .igm-overlay { animation: igmFade .18s ease both; }
        .igm-panel { animation: igmPop .32s cubic-bezier(.18,.84,.28,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .igm-overlay, .igm-panel { animation: none; }
        }
      `}</style>

      {/* role="dialog" lives on the card (not the backdrop). gt-igm-panel opts
          it out of global-theme's themed-dialog surface rule
          (.gt-themed [role="dialog"]:not([class*="gt-"])) so our own styling wins. */}
      <div
        className="igm-panel gt-igm-panel w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="igm-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="w-12 h-12 rounded-2xl bg-amber-50 ring-1 ring-amber-100 flex items-center justify-center mb-3">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
          </div>
          <h2 id="igm-title" className="text-lg font-bold text-gray-900">
            Can’t delete this {label}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {count === 1 ? "1 person is" : `${count} people are`} still using{" "}
            <span className="font-semibold text-gray-700">“{name}”</span>. Change their {label}{" "}
            first, then delete.
          </p>
        </div>

        {/* People */}
        <div className="px-6 pb-2 max-h-64 overflow-y-auto">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
            Tap to change their {label}
          </p>
          <div className="space-y-1.5">
            {people.map((p) => (
              <button
                key={p.id}
                onClick={() => onEditPerson(p.id)}
                className="group w-full flex items-center gap-3 rounded-xl border border-gray-100 px-2.5 py-2 hover:bg-emerald-50 hover:border-emerald-200 transition-colors text-left"
                title={`Edit ${p.name}`}
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
                  {p.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-800 truncate group-hover:text-emerald-700">
                    {p.name}
                  </div>
                  {p.sub ? (
                    <div className="text-[11px] text-gray-400 truncate">{p.sub}</div>
                  ) : null}
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 flex justify-end">
          <button
            onClick={onClose}
            className="text-sm font-semibold text-gray-600 hover:text-gray-800 px-4 h-10 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
